import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { branding, pageTitle } from '@/config/branding';
import { isDemoMode, isGoogleSignInEnabled } from '@/config/env';
import { listDemoProfiles, signIn, signInWithGoogle, type DemoProfile } from '@/domains/auth/auth-service';
import { homeRouteFor } from '@/domains/auth/permissions';
import { useSession } from '@/stores/session-store';
import { onGatewayProgress, requestDemoReset } from '@/lib/data';
import { BrandMark } from '@/components/brand-mark';
import {
  Button,
  Card,
  CardContent,
  Field,
  Input,
  InfoBanner,
  LoadingState,
} from '@/components/ui/primitives';
import { ConfirmDialog } from '@/components/ui/dialog';

const loginSchema = z.object({
  email: z.string().min(1, 'Indica la tua email.').email('Formato email non valido.'),
  password: isDemoMode ? z.string().optional() : z.string().min(1, 'Indica la password.'),
});

type LoginValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const { initialize, status, contexts, bootMessage, setBootMessage } = useSession();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [googlePending, setGooglePending] = useState(false);
  // Un accesso con Google annullato o rifiutato torna qui con l'errore nell'URL.
  const [oauthError] = useState(() => readOAuthError(window.location));
  // Al ritorno da Google la sessione si conferma in un istante: in quel
  // frattempo il modulo di accesso sembrerebbe chiedere di entrare di nuovo.
  const [returningFromGoogle] = useState(() => new URLSearchParams(window.location.search).has('code'));
  const sessionPending = !isDemoMode && status !== 'anonymous' && status !== 'error';

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  useEffect(() => {
    document.title = pageTitle('Accedi');
    // In modalità dimostrativa l'inizializzazione del database WASM richiede
    // qualche secondo: l'avanzamento viene mostrato invece di lasciare la
    // schermata apparentemente bloccata.
    onGatewayProgress((message) => setBootMessage(message));
    return () => onGatewayProgress(null);
  }, [setBootMessage]);

  // L'elenco dei profili dimostrativi esiste solo in modalità demo.
  const demoProfiles = useQuery<DemoProfile[]>({
    queryKey: ['demo-profiles'],
    queryFn: listDemoProfiles,
    enabled: isDemoMode,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (status !== 'authenticated') return;
    // Anche senza ristorante si prosegue: al ritorno da Google la schermata
    // «Nessuna organizzazione» spiega cosa fare, il modulo di accesso no.
    const first = contexts[0];
    navigate(first ? homeRouteFor(first.role_code, first.permissions) : '/app', { replace: true });
  }, [status, contexts, navigate]);

  const onGoogle = async (): Promise<void> => {
    setSubmitError(null);
    setGooglePending(true);
    try {
      await signInWithGoogle();
      // Il browser sta lasciando la pagina: lo stato di attesa resta visibile.
    } catch (error) {
      setGooglePending(false);
      setSubmitError(error instanceof Error ? error.message : 'Accesso con Google non riuscito.');
    }
  };

  const onSubmit = async (values: LoginValues): Promise<void> => {
    setSubmitError(null);
    try {
      await signIn(values.email, values.password ?? '');
      await initialize();
      const first = useSession.getState().contexts[0];
      navigate(first ? homeRouteFor(first.role_code, first.permissions) : '/app', { replace: true });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Accesso non riuscito.');
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <BrandMark showTagline />
        </div>

        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-1">
              <h1 className="text-xl font-semibold">Accedi a {branding.productName}</h1>
              <p className="text-sm text-muted-foreground">Gestione di sala, cucina, cassa e magazzino.</p>
            </div>

            {isDemoMode ? (
              <InfoBanner variant="warning">
                <p className="font-medium">Modalità dimostrativa</p>
                <p>
                  Il database gira interamente nel browser con i dati di prova dell’Osteria Aurora.
                  <strong> La password non viene verificata</strong> e nessun dato lascia questo dispositivo.
                  Non usare questa modalità con dati reali.
                </p>
              </InfoBanner>
            ) : null}

            {/* L'avanzamento della demo compare sopra il modulo senza sostituirlo:
                chi scrive l'email mentre il database si prepara non la perde. */}
            {bootMessage && bootMessage !== 'Pronto' ? <LoadingState label={bootMessage} /> : null}
            {sessionPending ? (
              <LoadingState
                label={returningFromGoogle ? 'Accesso con Google in corso…' : 'Verifica dell’accesso…'}
              />
            ) : (
              <>
                {oauthError ? (
                  <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                    {oauthError}
                  </p>
                ) : null}

                {isGoogleSignInEnabled ? (
                  <div className="space-y-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      className="w-full"
                      loading={googlePending}
                      onClick={() => void onGoogle()}
                    >
                      <GoogleMark />
                      Accedi con Google
                    </Button>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground" aria-hidden="true">
                      <span className="h-px flex-1 bg-border" />
                      oppure con email e password
                      <span className="h-px flex-1 bg-border" />
                    </div>
                  </div>
                ) : null}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                  <Field label="Email" htmlFor="email" error={errors.email?.message} required>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      autoFocus
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                      {...register('email')}
                    />
                  </Field>

                  <Field
                    label="Password"
                    htmlFor="password"
                    error={errors.password?.message}
                    required={!isDemoMode}
                    hint={isDemoMode ? 'Non richiesta in modalità dimostrativa.' : undefined}
                  >
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      aria-invalid={Boolean(errors.password)}
                      {...register('password')}
                    />
                  </Field>

                  {submitError ? (
                    <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                      {submitError}
                    </p>
                  ) : null}

                  <Button type="submit" className="w-full" size="lg" loading={isSubmitting}>
                    Accedi
                  </Button>
                </form>
              </>
            )}

            {isDemoMode && demoProfiles.data && demoProfiles.data.length > 0 ? (
              <div className="space-y-2 border-t pt-4">
                <p className="text-sm font-medium">Prova un ruolo</p>
                <div className="grid grid-cols-2 gap-2">
                  {demoProfiles.data.map((profile) => (
                    <button
                      key={profile.id}
                      type="button"
                      onClick={() => setValue('email', profile.email, { shouldValidate: true })}
                      className="rounded-md border p-2 text-left text-xs transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="block font-medium">{profile.role_name}</span>
                      <span className="block truncate text-muted-foreground">{profile.full_name}</span>
                    </button>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="px-0"
                  onClick={() => setConfirmReset(true)}
                >
                  Ripristina la demo da zero
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {isDemoMode ? (
          <ConfirmDialog
            open={confirmReset}
            onOpenChange={setConfirmReset}
            title="Ripristinare la demo?"
            description="Ordini, incassi e modifiche fatte in questo browser vengono cancellati e i dati di Osteria Aurora tornano quelli iniziali, compresi gli utenti di prova aggiunti di recente."
            confirmLabel="Ripristina"
            destructive
            onConfirm={requestDemoReset}
          />
        ) : null}

        <p className="text-center text-xs text-muted-foreground">
          Assistenza:{' '}
          <a className="underline" href={`mailto:${branding.company.supportEmail}`}>
            {branding.company.supportEmail}
          </a>
        </p>
      </div>
    </div>
  );
}

/** Logo di Google come richiesto dalle linee guida del pulsante di accesso. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" className="size-5">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** Messaggio leggibile per un accesso esterno non riuscito, se l'URL ne riporta uno. */
function readOAuthError(location: Location): string | null {
  const params = new URLSearchParams(location.search || location.hash.replace(/^#/, ''));
  const code = params.get('error');
  if (!code) return null;
  if (code === 'access_denied') return 'Accesso con Google annullato.';
  // Il dettaglio di Supabase aiuta a capire cosa è configurato male (indirizzi
  // di ritorno, provider disattivato). React lo mostra come testo, mai come HTML.
  const detail = params.get('error_description')?.slice(0, 200);
  return detail
    ? `Accesso con Google non riuscito: ${detail}`
    : 'Accesso con Google non riuscito. Riprova o usa email e password.';
}
